import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { trackLandingPage, trackLandingPageCta } from '../utils/analytics';
import { Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

const currentYear = new Date().getFullYear();
const faqItems = [
  {
    question: 'Is my photo stored anywhere?',
    answer: 'No. Photos are processed in real-time and deleted immediately after analysis. Nothing is saved.',
  },
  {
    question: 'How accurate are the recommendations?',
    answer: 'Trained on thousands of color combinations and color theory principles, tailored to your unique features.',
  },
  {
    question: 'Do I need a professional photo?',
    answer: 'Not at all. A clear selfie in natural daylight works perfectly.',
  },
  {
    question: 'How does Manatomy AI analyze my features?',
    answer: 'Our AI uses computer vision to analyze facial structure, skin undertones, hair color, and eye color from your photo, then matches them against extensive fashion and color theory databases.',
  },
  {
    question: 'How often should I update my analysis?',
    answer: 'We recommend updating if you make significant changes to your appearance like hair color or weight changes. Otherwise your style profile stays accurate for a long time.',
  },
];

const testimonials = [
  {
    stars: 5,
    text: 'Completely changed how I shop. The colors actually suit me.',
    name: 'Rohan S.',
  },
  {
    stars: 5,
    text: 'Felt like a personal stylist for free. Took 30 seconds.',
    name: 'Aman G.',
  },
  {
    stars: 5,
    text: 'My colleagues noticed the difference immediately.',
    name: 'Rahul B.',
  },
];

const StarIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="inline-block text-yellow-400"
    aria-hidden="true"
  >
    <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
  </svg>
);

const FaqItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-gray-200 py-4 max-w-xl mx-auto cursor-pointer"
      onClick={() => setOpen(!open)}
    >
      <div className="flex justify-between items-center gap-3">
        <span className="text-sm font-medium text-gray-900">{question}</span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        )}
      </div>
      {open && (
        <p className="text-xs text-gray-500 mt-3 leading-relaxed">{answer}</p>
      )}
    </div>
  );
};

const HomeV2: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    trackLandingPage();
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>AI Men's Styling Assistant | Color Analysis & Outfit Builder - Manatomy-AI</title>
        <meta
          name="description"
          content="Upload one photo. Get a complete outfit matched to your skin tone in 30 seconds. AI-powered personal styling for men."
        />
        <link rel="canonical" href="https://ai.themanatomy.com/" />
      </Helmet>

      {/* Nav */}
      <nav className="flex justify-between items-center px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-50">
        <div className="flex items-center gap-2">
          <img src="/images/Manatomy_favicon.png" width="24" alt="Manatomy AI" />
          <span className="text-base font-semibold tracking-wide text-gray-900">
            MANATOMY AI
          </span>
        </div>

        {/* Desktop nav links */}
        <div className="hidden md:flex gap-7 text-sm text-gray-500">
          <a href="#how-it-works" className="hover:text-gray-900 transition-colors">How it works</a>
          <a href="#testimonials" className="hover:text-gray-900 transition-colors">Reviews</a>
          <a href="#faq" className="hover:text-gray-900 transition-colors">FAQ</a>
        </div>

        <Link
          to="/quick-start"
          onClick={() => trackLandingPageCta('header')}
          className="bg-gray-900 text-white text-xs font-medium px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
        >
          Get started
        </Link>
      </nav>

      {/* Hero */}
      <section
        className="px-6 py-12 text-center"
        style={{ background: 'linear-gradient(180deg, #fdf3ee 0%, #fdf0ea 40%, #fdf8f5 100%)' }}
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3.5 py-1.5 text-xs text-gray-500 mb-5">
          <Sparkles className="w-3 h-3" aria-hidden="true" />
          AI-powered personal styling
        </div>

        {/* Heading */}
        <h1
          className="text-3xl font-medium leading-snug text-gray-900 mb-3 max-w-sm mx-auto"
        >
          Stop wearing the wrong colors.{' '}
          <em className="not-italic" style={{ color: '#a8693f' }}>
            Discover what actually suits you.
          </em>
        </h1>

        <p className="text-sm text-gray-500 max-w-xs mx-auto mb-7 leading-relaxed">
          Upload one photo. Get a complete outfit matched to your skin tone in 30 seconds.
        </p>

        <Link
          to="/quick-start"
          onClick={() => trackLandingPageCta('hero_banner')}
          className="inline-block bg-gray-900 text-white text-sm font-medium px-8 py-3.5 rounded-lg mb-2.5 hover:bg-gray-700 transition-colors"
        >
          Reveal my best colors →
        </Link>

        <p className="text-xs text-gray-400 mb-8">Free · No card needed · Photo deleted instantly</p>

        {/* Stats */}
        <div className="flex justify-center gap-9 pt-6 border-t border-black/[0.06] max-w-xs mx-auto">
          <div className="text-center">
            <div className="text-xl font-medium text-gray-900">50K+</div>
            <div className="text-[10px] text-gray-400 mt-0.5">Styled</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-medium text-gray-900">98%</div>
            <div className="text-[10px] text-gray-400 mt-0.5">Satisfaction</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-medium text-gray-900">4.9★</div>
            <div className="text-[10px] text-gray-400 mt-0.5">Rating</div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="px-6 py-10">
        <p
          className="text-xl font-medium text-gray-900 text-center mb-2"
        >
          Three steps. Thirty seconds.
        </p>
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto mt-7">
          {[
            { num: '1', title: 'Upload a photo', desc: 'Just your face, natural light' },
            { num: '2', title: 'AI reads your tone', desc: 'Skin, eyes, hair analysed instantly' },
            { num: '3', title: 'Get your outfit', desc: 'Shirt, pant and shoes matched' },
          ].map((step) => (
            <div key={step.num} className="text-center">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mx-auto mb-3"
                style={{ background: '#fdf0ea', color: '#a8693f' }}
              >
                {step.num}
              </div>
              <div className="text-xs font-medium text-gray-900 mb-1">{step.title}</div>
              <div className="text-[11px] text-gray-400 leading-snug">{step.desc}</div>
            </div>
          ))}
        </div>

        {/* Outfits illustration */}
        <div className="max-w-lg mx-auto mt-10">
          <img
            src="/images/outfits.png"
            alt="AI outfit analysis process"
            className="w-full h-auto rounded-xl shadow-sm"
          />
        </div>
      </section>

      {/* Proof / Before-After */}
      <section className="px-6 py-10 bg-gray-50">
        <p
          className="text-xl font-medium text-gray-900 text-center mb-2"
        >
          See the difference
        </p>
        <p className="text-sm text-gray-400 text-center mb-7 max-w-xs mx-auto">
          Same man, two color choices. One look just works.
        </p>
        <div className="max-w-md mx-auto">
          <img
            src="/images/ai-img.png"
            alt="Before and After – AI color matched styling comparison"
            className="w-full h-auto rounded-xl shadow-sm"
          />
        </div>

        {/* Personalized recommendations image */}
        <div className="max-w-md mx-auto mt-8">
          <img
            src="/images/suggestions-img.png"
            alt="Personalized style recommendations"
            className="w-full h-auto rounded-xl shadow-sm"
          />
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="px-6 py-10">
        <p
          className="text-xl font-medium text-gray-900 text-center mb-7"
        >
          What men are saying
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 max-w-xl mx-auto">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="flex gap-0.5 mb-2">
                {[...Array(t.stars)].map((_, s) => <StarIcon key={s} />)}
              </div>
              <p className="text-xs text-gray-700 leading-relaxed mb-3">{t.text}</p>
              <p className="text-[10.5px] text-gray-400 font-medium">{t.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gray-900 text-center px-6 py-12">
        <h2
          className="text-white text-xl font-medium mb-2"
        >
          Ready to find your colors?
        </h2>
        <p className="text-white/70 text-xs mb-6">Join 50,000+ men who upgraded their style with AI</p>
        <Link
          to="/quick-start"
          onClick={() => trackLandingPageCta('bottom_banner')}
          className="inline-block bg-white text-gray-900 text-sm font-medium px-8 py-3.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Start your style journey →
        </Link>
      </section>

      {/* Premium collection */}
      <section className="px-6 py-10 bg-gray-50">
        <p
          className="text-xl font-medium text-gray-900 text-center mb-2"
        >
          Premium Manatomy Collection
        </p>
        <p className="text-sm text-gray-400 text-center mb-7 max-w-xs mx-auto">
          Curated clothing designed to complement your AI-recommended style.
        </p>
        <div className="max-w-xl mx-auto relative">
          <img
            src="/images/premium-img.png"
            alt="Manatomy Premium Collection – Shirts, Belts, Pants, and Shoes"
            className="w-full h-auto rounded-xl shadow-sm"
          />
          <a href="https://themanatomy.com/collections/topwear" target="_blank" rel="noreferrer" className="absolute top-0 left-0 w-1/2 h-1/2" aria-label="Manatomy Shirts" />
          <a href="https://themanatomy.com/products/black-legacy-belt" target="_blank" rel="noreferrer" className="absolute top-0 right-0 w-1/2 h-1/2" aria-label="Legacy Belt" />
          <a href="https://themanatomy.com/collections/bottomwear" target="_blank" rel="noreferrer" className="absolute bottom-0 left-0 w-1/2 h-1/2" aria-label="Manatomy Pants" />
          <a href="https://themanatomy.com/products/black-legacy-premium-italian-leather-shoes" target="_blank" rel="noreferrer" className="absolute bottom-0 right-0 w-1/2 h-1/2" aria-label="Legacy Shoes" />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 py-10">
        <p
          className="text-xl font-medium text-gray-900 text-center mb-7"
        >
          Common questions
        </p>
        <div>
          {faqItems.map((item, i) => (
            <FaqItem key={i} question={item.question} answer={item.answer} />
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-gray-100 px-6">
        <div className="max-w-xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/images/Manatomy_favicon.png" width="22" alt="Manatomy AI" />
              <span className="text-sm font-semibold text-gray-900">
                Manatomy AI
              </span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed mb-2">AI-powered personal styling for the modern man.</p>
            <p className="text-xs text-gray-400">Phone: <a href="tel:917065787678" className="hover:text-gray-700">+91 70657 87678</a></p>
            <p className="text-xs text-gray-400 mt-1">Email: <a href="mailto:help@themanatomy.com" className="hover:text-gray-700">help@themanatomy.com</a></p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Collections</h4>
            <ul className="space-y-1.5">
              <li><a href="https://themanatomy.com/collections/topwear" target="_blank" rel="noreferrer" className="text-xs text-gray-400 hover:text-gray-700">Topwear</a></li>
              <li><a href="https://themanatomy.com/collections/bottomwear" target="_blank" rel="noreferrer" className="text-xs text-gray-400 hover:text-gray-700">Bottomwear</a></li>
              <li><a href="https://themanatomy.com/collections/shoes-belt" target="_blank" rel="noreferrer" className="text-xs text-gray-400 hover:text-gray-700">Shoes & Belt</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Policies</h4>
            <ul className="space-y-1.5">
              <li><a href="/terms-of-service" className="text-xs text-gray-400 hover:text-gray-700">Terms of Service</a></li>
              <li><a href="/privacy-policy" className="text-xs text-gray-400 hover:text-gray-700">Privacy Policy</a></li>
              <li><a href="/cookie-policy" className="text-xs text-gray-400 hover:text-gray-700">Cookie Policy</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">© {currentYear} - The Manatomy | All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomeV2;
